import React, { useEffect, useState, ViewTransition } from 'react';
import { View, Text, Pressable, Image } from '../lib/react-native-compat';
import { selectedTab, userIdAtom, userEmailCheckAtom, userNameAtom, userRoleAtom, previewRoleAtom, selectedJobAtom, selectedJobDataAtom, projectCompleteAtom, selectedVersionAtom, overheadDataAtom, jobCostDetailCondensedAtom, jobUserDataAtom, dataSourceVersionAtom, jobOpenCommitmentsAtom, incentiveEligibleAtom, incentiveEligibleAmountAtom, incentiveCalcBreakdownAtom, incentiveRateAtom, sideMenuBudgetsAtom, sideMenuCostsAtom } from '../store/company';
import { useAtom } from 'jotai';
import { setSelectedTabStrorage, pNum, fNum, apiUrl, isFusionAdmin, isProjectCoordinator, effectiveIsProjectCoordinator, effectiveIsBuyer, pcAllowedTabs, allowedTabsForRole } from '../db/company';
import { fieldBonusRemoved } from '../helpers/fieldBonus';
import { isVoltaGridJob } from '../helpers/voltaGrid';
import moment from 'moment';

// Import assets
// The Fusion logo redrawn in the Strike AI template colours (docs/STRIKE_STYLE.md, section 8)
import strike from '../assets/fusion-logo.svg';
import favicon from '../assets/fusion-mark.svg';
import dashboard from '../assets/dashboard.svg';
import forecast from '../assets/forecast.svg';
import overview from '../assets/overview.svg';
import summary from '../assets/summary.svg';
import productivity from '../assets/productivity.svg';
import reports from '../assets/reports.svg';
import budget from '../assets/budget.svg';
import colog from '../assets/colog.svg';
import lIcon from '../assets/Profile.svg';
import eIcon from '../assets/Truck.svg';
import mIcon from '../assets/Box.png';
import backhoeIcon from '../assets/backhoe.png';
import sIcon from '../assets/Two People.svg';
import oIcon from '../assets/Tag.svg';
import contingencyIcon from '../assets/Weather Rain.svg';
import adminIcon from '../assets/admin.svg';
import exportIcon from '../assets/export.svg';

const SideMenu = () => {
    const [versions, setVersions] = useAtom(dataSourceVersionAtom);
    const [tab, setTab] = useAtom(selectedTab);
    const [userId, setUserId] = useAtom(userIdAtom);
    const [userEmailCheck, setUserEmailCheck] = useAtom(userEmailCheckAtom);
    const [userNameValue] = useAtom(userNameAtom);
    const [userRole] = useAtom(userRoleAtom);
    const [previewRole] = useAtom(previewRoleAtom);
    const [selectedJob] = useAtom(selectedJobAtom);
    const [selectedVersion] = useAtom(selectedVersionAtom);
    const [selectedJobData, setSelectedJobData] = useAtom(selectedJobDataAtom);
    const [overheadData, setOverheadData] = useAtom(overheadDataAtom);
    const [jobCostDetail] = useAtom(jobCostDetailCondensedAtom);
    const [jobUserData, setJobUserData] = useAtom(jobUserDataAtom);
    const [projectComplete, setProjectComplete] = useAtom(projectCompleteAtom);
    const [jobOpenCommitments, setJobOpenCommitments] = useAtom(jobOpenCommitmentsAtom);
    const [, setIncentiveEligible] = useAtom(incentiveEligibleAtom);
    const [, setIncentiveEligibleAmount] = useAtom(incentiveEligibleAmountAtom);
    const [, setIncentiveCalcBreakdown] = useAtom(incentiveCalcBreakdownAtom);
    const [incentiveRateValue, setIncentiveRate] = useAtom(incentiveRateAtom);
    const [, setSideMenuBudgets] = useAtom(sideMenuBudgetsAtom);
    const [, setSideMenuCosts] = useAtom(sideMenuCostsAtom);

    // Project Coordinators see a restricted menu (procurement tabs only).
    // Identify by DB role OR the static fallback list (see db/company.js) so the
    // restriction holds even when only the login fallback is working. An active
    // admin role-preview overrides this (see previewRole / the switcher below).
    const isPC = effectiveIsProjectCoordinator(previewRole, userRole, userEmailCheck);
    // Buyers get a menu of just the two portfolio procurement tabs.
    const isBuyerUser = effectiveIsBuyer(previewRole, userRole, userEmailCheck);
    const allowedTabs = allowedTabsForRole(previewRole, userRole, userEmailCheck);

    // Bounce a restricted user off any tab they may not see so the content area
    // can't render it.
    useEffect(() => {
        if (allowedTabs && tab && !allowedTabs.includes(tab)) {
            setTab(allowedTabs[0]);
            setSelectedTabStrorage(allowedTabs[0]);
        }
    }, [allowedTabs, tab]);

    const [ohPercent, setOverheadPercent] = useState();
    const [versionInfo, setVersionInfo] = useState({});
    const [coValues, setCoValues] = useState({});
    // Field Bonus (51800) is retired for versions June 18, 2026 forward — its
    // ETC no longer counts toward the labor EAC totals for those versions.
    const hideFieldBonus = fieldBonusRemoved(versionInfo?.Version_Start_DateTime);

    const [budgets, setBudgets] = useState({
        contractValuesRevised: 0,
        changeOrderRevenue: 0,
        laborDayZeroBudget: 0,
        equipmentDayZeroBudget: 0,
        materialsDayZeroBudget: 0,
        subcontractorsDayZeroBudget: 0,
        otherExpensesDayZeroBudget: 0,
        contingencyDayZeroBudget: 0,
        overheadDayZeroBudget: 0,
        typeBudgets: {}
    });

    const [costs, setCosts] = useState({
        contingency: 0,
        labor: 0,
        materials: 0,
        rentals: 0,
        subs: 0,
        other: 0,
        equipment: 0,
        overhead: 0,
        totalCost: 0
    });

    const [adminMenuOpen, setAdminMenuOpen] = useState(() => {
        try {
            const stored = localStorage.getItem('sideMenuAdminOpen');
            if (stored != null) return JSON.parse(stored);
        } catch (e) { }
        return false;
    });

    useEffect(() => {
        try {
            localStorage.setItem('sideMenuAdminOpen', JSON.stringify(adminMenuOpen));
        } catch (e) { }
    }, [adminMenuOpen]);

    const [isCollapsed, setIsCollapsed] = useState(() => {
        try {
            const stored = localStorage.getItem('sideMenuCollapsed');
            if (stored != null) return JSON.parse(stored);
        } catch (e) { }
        return window.innerWidth <= 768;
    });

    useEffect(() => {
        try {
            localStorage.setItem('sideMenuCollapsed', JSON.stringify(isCollapsed));
        } catch (e) { }
    }, [isCollapsed]);

    useEffect(() => {
        if (selectedJobData && selectedJobData.Cat_Code_BU_27) {
            setOverheadPercent(overheadData[selectedJobData.Cat_Code_BU_27] ? overheadData[selectedJobData.Cat_Code_BU_27] : '');
        }
    }, [selectedJobData, overheadData]);

    // Extract incentive rate from jobUserData on every load/job switch
    useEffect(() => {
        if (jobUserData && jobUserData.length > 0) {
            jobUserData.forEach((data) => {
                if (data.Forecast_Tab === 'Forecast' && data.Forecast_Field_Name === 'incentiveRate') {
                    if (data.Forecast_Value != null && data.Forecast_Value !== '') {
                        setIncentiveRate(data.Forecast_Value);
                    }
                }
            });
        }
    }, [jobUserData]);

    useEffect(() => {
        calculateContractAmounts();
    }, [jobUserData, coValues, jobCostDetail, ohPercent, jobOpenCommitments, incentiveRateValue, versionInfo, hideFieldBonus]);

    useEffect(() => {
        versions && versions.length > 0 && setVersionInfo(versions.find(v => v.Version_GUID === selectedVersion));
    }, [versions, selectedVersion]);

    const calculateContractAmounts = () => {
        let contractValuesRevised = 0;
        let changeOrderRevenue = 0;
        let laborDayZeroBudget = 0;
        let equipmentDayZeroBudget = 0;
        let materialsDayZeroBudget = 0;
        let subcontractorsDayZeroBudget = 0;
        let otherExpensesDayZeroBudget = 0;
        let contingencyDayZeroBudget = 0;
        let overheadDayZeroBudget = 0;
        let revenueJTD = 0;
        let revenueJTDPrevPeriod = 0;
        let laborJTD = 0;
        let laborHoursJTD = 0;
        let equipmentJTD = 0;
        let materialsJTD = 0;
        let subcontractorsJTD = 0;
        let externalSubsJTD = 0;
        let otherExpensesJTD = 0;
        let ohJTD = 0;

        const dayZeroValues = {};
        const chOrValues = {};
        const contingData = {};
        const phasedLabor = {};
        const phasedEquipment = {};
        const equipmentValues = {};
        const laborValues = {};
        const etcValues = {};
        const otherEtcValues = {};

        let regularTime = 0;
        let truckPay = 0;
        let weldingRigPay = 0;
        let equipment = 0;
        let outsideEquipmentRentals = 0;
        let materials = 0;
        let mats = 0;
        let toolsSupplies = 0;
        let skids = 0;
        let internalSubs = 0;
        let externalSubs = 0;
        let haulingTransportation = 0;
        let personalMeals = 0;
        let teamMealsEntertain = 0;
        let airfare = 0;
        let lodging = 0;
        let fieldOfficeRent = 0;
        let fieldOfficeUtility = 0;
        let fieldOfficeExpense = 0;
        let labFeesTesting = 0;
        let safetyPPE = 0;
        let bondsInsurancePremiums = 0;
        let permits = 0;
        let insuranceClaims = 0;
        let taxes = 0;
        let accruedExpenses = 0;
        let warrantyReserve = 0;
        let misc = 0;
        let fieldBonus = 0;
        let fuel = 0;
        let repairs = 0;
        let otherTravelExpense = 0;

        if (jobUserData && jobUserData.length > 0) {
            jobUserData.forEach((d) => {
                if (d?.Forecast_Tab === 'Change Order') {
                    chOrValues[d?.Forecast_User_Data_GUID || d?.Forecast_Row_ID] = { ...d, [d.Cost_Type]: d.Value_Amount, id: d.Row_Label }
                }

                if (d?.Forecast_Tab === 'Day Zero Budget') {
                    dayZeroValues[d?.Forecast_Row_ID] = d;
                }

                if (d?.Forecast_Tab === 'Burn Tool Equipment Phased') {
                    phasedEquipment[d?.Forecast_Row_ID] = { id: d?.Forecast_Row_ID, ...d };
                }

                if (d?.Forecast_Tab === 'Burn Tool Labor Phased') {
                    phasedLabor[d?.Forecast_Row_ID] = { id: d?.Forecast_Row_ID, ...d };
                }

                if (d?.Forecast_Tab === 'Burn Tool Equipment') {
                    equipmentValues[d?.Forecast_Row_ID] = d;
                }

                if (d?.Forecast_Tab === 'Burn Tool Labor') {
                    laborValues[d?.Forecast_Row_ID] = d;
                }

                if (d?.Forecast_Tab === 'Contingency') {
                    if (contingData[d?.Forecast_Row_ID]) {
                        contingData[d?.Forecast_Row_ID][d.Forecast_Field_Name] = d.Forecast_Value;
                    } else {
                        contingData[d?.Forecast_Row_ID] = {
                            id: d?.Forecast_Row_ID,
                            [d.Forecast_Field_Name]: d.Forecast_Value
                        };
                    }
                }

                if (d?.Cost_Type && d?.Forecast_Field_Name == 'etc' && (!d?.Cost_Code || d?.Cost_Code != '101101')) {
                    if (etcValues[d?.Cost_Type]) {
                        etcValues[d?.Cost_Type] = etcValues[d?.Cost_Type] + pNum(d?.Value_Amount);
                    } else {
                        etcValues[d?.Cost_Type] = pNum(d?.Value_Amount);
                    }
                } else if (d?.Cost_Type && d?.Forecast_Field_Name == 'etc' && d?.Cost_Code == '101101') {
                    if (otherEtcValues[d?.Cost_Type]) {
                        otherEtcValues[d?.Cost_Type] = otherEtcValues[d?.Cost_Type] + pNum(d?.Value_Amount);
                    } else {
                        otherEtcValues[d?.Cost_Type] = pNum(d?.Value_Amount);
                    }
                }
            });

            let totalRegularTime = 0, totalOvertime = 0, totalBurden = 0, totalByod = 0, totalPerDiem = 0;
            let totalTruckPay = 0, totalWeldingRigPay = 0, totalFieldBonus = 0;
            let totalInsideEquipmentRentals = 0, totalOutsideEquipmentRentals = 0, totalFuel = 0, totalRepairs = 0;
            let totalMaterials = 0, totalMats = 0, totalToolsSupplies = 0, totalSkids = 0;
            let totalInternalSubcontractors = 0, totalExternalSubcontractors = 0, totalHaulingTransportation = 0;
            let totalPersonalMeals = 0, totalTeamMealsEntertain = 0, totalAirfare = 0, totalLodging = 0;
            let totalOtherTravelExpense = 0, totalFieldOfficeRent = 0, totalFieldOfficeUtility = 0;
            let totalFieldOfficeExpense = 0, totalLabFeesTesting = 0, totalSafetyPPE = 0;
            let totalBondsInsurancePremiums = 0, totalPermits = 0, totalInsuranceClaims = 0;
            let totalTaxes = 0, totalAccruedExpenses = 0, totalWarrantyReserve = 0, totalMisc = 0;
            let totalUnitPriceAdders = 0, totalTAndM = 0, totalChangeOrders = 0, totalReductionOfScope = 0;
            let totalcLabor = 0, totalcEquipment = 0, totalcMaterials = 0;
            let totalcSubcontractors = 0, totalcOtherExpenses = 0, totalcTotal = 0, totalContingency = 0;

            //console.log('chOrValues:', chOrValues);
            Object.values(chOrValues).forEach((row) => {
                if (row.Status === 'Approved') {
                    if (row.Cost_Code == '101101') {
                        const coFields = Object.keys(row);
                        coFields.forEach((field) => {
                            const keyPattern = /^\d{5}$/;
                            if (keyPattern.test(field)) {
                                totalContingency = totalContingency + (isNaN(pNum(row[field])) ? 0 : pNum(row[field]));
                            }
                        });
                    } else {
                        totalUnitPriceAdders += (isNaN(pNum(row['41000'])) ? 0 : pNum(row['41000']));
                        // totalTAndM += (isNaN(pNum(row.tAndM)) ? 0 : pNum(row.tAndM));
                        // totalChangeOrders += (isNaN(pNum(row.changeOrders)) ? 0 : pNum(row.changeOrders));
                        // totalReductionOfScope += (isNaN(pNum(row.reductionOfScope)) ? 0 : pNum(row.reductionOfScope));

                        totalRegularTime += (isNaN(pNum(row['51100'])) ? 0 : pNum(row['51100']));
                        totalOvertime += (isNaN(pNum(row['51200'])) ? 0 : pNum(row['51200']));
                        totalBurden += (isNaN(pNum(row['51300'])) ? 0 : pNum(row['51300']));
                        totalByod += (isNaN(pNum(row['51400'])) ? 0 : pNum(row['51400']));
                        totalPerDiem += (isNaN(pNum(row['51500'])) ? 0 : pNum(row['51500']));
                        totalTruckPay += (isNaN(pNum(row['51600'])) ? 0 : pNum(row['51600']));
                        totalWeldingRigPay += (isNaN(pNum(row['51700'])) ? 0 : pNum(row['51700']));
                        totalFieldBonus += (isNaN(pNum(row['51800'])) ? 0 : pNum(row['51800']));
                        totalInsideEquipmentRentals += (isNaN(pNum(row['52100'])) ? 0 : pNum(row['52100']));
                        totalOutsideEquipmentRentals += (isNaN(pNum(row['52200'])) ? 0 : pNum(row['52200']));
                        totalFuel += (isNaN(pNum(row['52300'])) ? 0 : pNum(row['52300']));
                        totalRepairs += (isNaN(pNum(row['52400'])) ? 0 : pNum(row['52400']));
                        totalMaterials += (isNaN(pNum(row['53100'])) ? 0 : pNum(row['53100']));
                        totalMats += (isNaN(pNum(row['53200'])) ? 0 : pNum(row['53200']));
                        totalToolsSupplies += (isNaN(pNum(row['53300'])) ? 0 : pNum(row['53300']));
                        totalSkids += (isNaN(pNum(row['53400'])) ? 0 : pNum(row['53400']));
                        totalInternalSubcontractors += (isNaN(pNum(row['54100'])) ? 0 : pNum(row['54100']));
                        totalExternalSubcontractors += (isNaN(pNum(row['54200'])) ? 0 : pNum(row['54200']));
                        totalHaulingTransportation += (isNaN(pNum(row['54300'])) ? 0 : pNum(row['54300']));
                        totalPersonalMeals += (isNaN(pNum(row['55100'])) ? 0 : pNum(row['55100']));
                        totalTeamMealsEntertain += (isNaN(pNum(row['55200'])) ? 0 : pNum(row['55200']));
                        totalAirfare += (isNaN(pNum(row['55300'])) ? 0 : pNum(row['55300']));
                        totalLodging += (isNaN(pNum(row['55400'])) ? 0 : pNum(row['55400']));
                        totalOtherTravelExpense += (isNaN(pNum(row['55900'])) ? 0 : pNum(row['55900']));
                        totalFieldOfficeRent += (isNaN(pNum(row['56100'])) ? 0 : pNum(row['56100']));
                        totalFieldOfficeUtility += (isNaN(pNum(row['56200'])) ? 0 : pNum(row['56200']));
                        totalFieldOfficeExpense += (isNaN(pNum(row['56300'])) ? 0 : pNum(row['56300']));
                        totalLabFeesTesting += (isNaN(pNum(row['56400'])) ? 0 : pNum(row['56400']));
                        totalSafetyPPE += (isNaN(pNum(row['56500'])) ? 0 : pNum(row['56500']));
                        totalBondsInsurancePremiums += (isNaN(pNum(row['56600'])) ? 0 : pNum(row['56600']));
                        totalPermits += (isNaN(pNum(row['56700'])) ? 0 : pNum(row['56700']));
                        totalInsuranceClaims += (isNaN(pNum(row['56800'])) ? 0 : pNum(row['56800']));
                        totalTaxes += (isNaN(pNum(row['57100'])) ? 0 : pNum(row['57100']));
                        totalAccruedExpenses += (isNaN(pNum(row['57200'])) ? 0 : pNum(row['57200']));
                        totalWarrantyReserve += (isNaN(pNum(row['57300'])) ? 0 : pNum(row['57300']));
                        totalMisc += (isNaN(pNum(row['57900'])) ? 0 : pNum(row['57900']));
                    }
                }
            });

            changeOrderRevenue = totalUnitPriceAdders + totalTAndM + totalChangeOrders + totalReductionOfScope;
            laborDayZeroBudget = totalRegularTime + totalOvertime + totalBurden + totalByod + totalPerDiem + totalTruckPay + totalWeldingRigPay + totalFieldBonus;
            equipmentDayZeroBudget = totalInsideEquipmentRentals + totalOutsideEquipmentRentals + totalFuel + totalRepairs;
            materialsDayZeroBudget = totalMaterials + totalMats + totalToolsSupplies + totalSkids;
            subcontractorsDayZeroBudget = totalInternalSubcontractors + totalExternalSubcontractors + totalHaulingTransportation;
            otherExpensesDayZeroBudget = totalPersonalMeals + totalTeamMealsEntertain + totalAirfare + totalLodging + totalOtherTravelExpense + totalFieldOfficeRent + totalFieldOfficeUtility + totalFieldOfficeExpense + totalLabFeesTesting + totalSafetyPPE + totalBondsInsurancePremiums + totalPermits + totalInsuranceClaims + totalTaxes + totalAccruedExpenses + totalWarrantyReserve + totalMisc;
            contingencyDayZeroBudget += totalContingency;

            const actualTypes = {};
            const typeBudgets = {};
            // Seed typeBudgets with approved CO amounts so per-type lookups (e.g. rentals 52200) include COs
            Object.values(chOrValues).forEach((row) => {
                if (row.Status === 'Approved' && row.Cost_Code != '101101') {
                    Object.keys(row).forEach((field) => {
                        if (/^\d{5}$/.test(field) && field[0] === '5') {
                            const v = isNaN(pNum(row[field])) ? 0 : pNum(row[field]);
                            if (v) typeBudgets[field] = (typeBudgets[field] || 0) + v;
                        }
                    });
                }
            });
            Object.values(dayZeroValues).forEach((row) => {
                if (row['Cost_Type'] && row['Cost_Code'] != '101101') {
                    if (row['Cost_Type'][0] == '4') {
                        contractValuesRevised += pNum(row.Value_Amount);
                    }
                    if (row['Cost_Type'] >= '51000' && row['Cost_Type'] <= '51999') {
                        laborDayZeroBudget += pNum(row.Value_Amount);
                    }
                    if (row['Cost_Type'] >= '52000' && row['Cost_Type'] <= '52999') {
                        equipmentDayZeroBudget += pNum(row.Value_Amount);
                    }
                    if (row['Cost_Type'] >= '53000' && row['Cost_Type'] <= '53999') {
                        materialsDayZeroBudget += pNum(row.Value_Amount);
                    }
                    if (row['Cost_Type'] >= '54000' && row['Cost_Type'] <= '54999') {
                        subcontractorsDayZeroBudget += pNum(row.Value_Amount);
                    }
                    if (row['Cost_Type'] >= '55000' && row['Cost_Type'] <= '58000') {
                        otherExpensesDayZeroBudget += pNum(row.Value_Amount);
                    }
                    if (row['Cost_Type'] == '60900') {
                        overheadDayZeroBudget += pNum(row.Value_Amount);
                    }
                    if (typeBudgets[row['Cost_Type']]) {
                        typeBudgets[row['Cost_Type']] += pNum(row.Value_Amount);
                    } else {
                        typeBudgets[row['Cost_Type']] = pNum(row.Value_Amount);
                    }
                } else {
                    if (row['Cost_Type'] && row['Cost_Type'][0] == '5') {
                        contingencyDayZeroBudget += pNum(row.Value_Amount);
                    }
                }
            });

            Object.values(contingData).forEach((row) => {
                totalcLabor += (isNaN(pNum(row.CurrentLabor)) ? 0 : pNum(row.CurrentLabor));
                totalcEquipment += (isNaN(pNum(row.CurrentEquipment)) ? 0 : pNum(row.CurrentEquipment));
                totalcMaterials += (isNaN(pNum(row.CurrentMaterials)) ? 0 : pNum(row.CurrentMaterials));
                totalcSubcontractors += (isNaN(pNum(row.CurrentSubs)) ? 0 : pNum(row.CurrentSubs));
                totalcOtherExpenses += (isNaN(pNum(row.CurrentOther)) ? 0 : pNum(row.CurrentOther));
            });
            totalcTotal = totalcLabor + totalcEquipment + totalcMaterials + totalcSubcontractors + totalcOtherExpenses;

            const budgetPayload = {
                contractValuesRevised,
                changeOrderRevenue,
                laborDayZeroBudget,
                equipmentDayZeroBudget,
                materialsDayZeroBudget,
                subcontractorsDayZeroBudget,
                otherExpensesDayZeroBudget,
                contingencyDayZeroBudget,
                overheadDayZeroBudget,
                typeBudgets
            };
            setBudgets(budgetPayload);
            setSideMenuBudgets(budgetPayload);

            let lastFoundOHDate = moment('01/01/1900', 'MM/DD/YYYY');
            jobCostDetail?.length && jobCostDetail?.forEach((row) => {
                if (row.Cost_Type_Code >= '40000' && row.Cost_Type_Code <= '49999') {
                    revenueJTD += row.Job_Cost;
                    if (moment(row.GL_Date).isBefore(moment(versionInfo?.Version_Start_DateTime).add('-1', 'Month').endOf('Month').format('YYYY-MM-DD'))) {
                        revenueJTDPrevPeriod += row.Job_Cost;
                    }
                }
                if (row.Cost_Type_Code >= '51000' && row.Cost_Type_Code <= '51999') {
                    laborJTD += row.Job_Cost;
                    laborHoursJTD += row.Total_Hours;
                }
                if (row.Cost_Type_Code >= '52000' && row.Cost_Type_Code <= '52999') {
                    equipmentJTD += row.Job_Cost;
                }
                if (row.Cost_Type_Code >= '53000' && row.Cost_Type_Code <= '53999') {
                    materialsJTD += row.Job_Cost;
                }
                if (row.Cost_Type_Code >= '54000' && row.Cost_Type_Code <= '54999') {
                    subcontractorsJTD += row.Job_Cost;
                }
                if (row.Cost_Type_Code >= '54200' && row.Cost_Type_Code <= '54300') {
                    externalSubsJTD += row.Job_Cost;
                }
                if (row.Cost_Type_Code >= '55000' && row.Cost_Type_Code <= '58000') {
                    otherExpensesJTD += row.Job_Cost;
                }
                if (row.Cost_Type_Code == '60900') {
                    ohJTD += pNum(row.Job_Cost);
                    if (lastFoundOHDate.isBefore(row.GL_Date)) {
                        lastFoundOHDate = moment(row.GL_Date);
                    }
                }
                if (actualTypes[row.Cost_Type_Code]) {
                    actualTypes[row.Cost_Type_Code].value += row.Job_Cost;
                } else {
                    actualTypes[row.Cost_Type_Code] = { name: row.Cost_Type_Code, value: row.Job_Cost };
                }
            });

            Object.keys(equipmentValues).forEach((key) => {
                if (key.length == 10 && moment(key).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(projectComplete).endOf('week').isAfter(key)) {
                    const row = equipmentValues[key];
                    equipment += ((isNaN(pNum(row.Rate)) ? 0 : pNum(row.Rate)) * (isNaN(pNum(row.Number_Units)) ? 0 : pNum(row.Number_Units)) * (isNaN(pNum(row.Number_Hours)) ? 0 : pNum(row.Number_Hours)));
                }
            });

            Object.values(laborValues).forEach((row) => {
                const week = row.Forecast_Row_ID.split('*')[0];
                if (moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(projectComplete).isAfter(week)) {
                    regularTime += (!isNaN(pNum(row.Rate)) && !isNaN(pNum(row.Number_Units)) && !isNaN(pNum(row.Number_Hours)) ? pNum(row.Rate) * pNum(row.Number_Units) * pNum(row.Number_Hours) : 0);
                }
            });

            Object.keys(phasedEquipment).sort().forEach((t) => {
                const r = phasedEquipment[t];
                const pC = projectComplete;
                const v = versionInfo?.Version_Start_DateTime;
                // Row IDs use '=' (new) or '*' (legacy) between name and week
                const w = t.includes('=') ? t.split('=').pop() : t.split('*')[1];
                const rate = r?.Rate;
                const h = r?.Number_Hours;
                const n = r?.Number_Units;
                if (w && h && n && rate && moment(w).isAfter(moment(v).startOf('week')) && moment(w).isBefore(moment(pC).endOf('week'))) {
                    equipment += (!isNaN(pNum(rate)) && !isNaN(pNum(h)) && !isNaN(pNum(n)) ? pNum(rate) * pNum(h) * pNum(n) : 0);
                }
            });

            Object.keys(phasedLabor).forEach((row) => {
                const weekRow = row.includes('=');
                const week = row.split('=')[1];
                const withinProject = moment(week).isAfter(moment(versionInfo?.Version_Start_DateTime).startOf('week')) && moment(week).isBefore(moment(projectComplete).endOf('week'));
                const h = phasedLabor[row]?.Number_Hours;
                const bR = phasedLabor[row]?.Rate;
                const cS = phasedLabor[row]?.Number_Units;
                if (weekRow && phasedLabor[row] && h && bR && cS && withinProject) {
                    regularTime += (!isNaN(pNum(h)) && !isNaN(pNum(bR)) && !isNaN(pNum(cS)) ? pNum(h) * pNum(bR) * pNum(cS) : 0);
                }
            });

            Object.keys(etcValues).forEach((row) => {
                if (row == '51600') {
                    truckPay += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '51700') {
                    weldingRigPay += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '52100') {
                    equipment += (isNaN(etcValues[row]) ? 0 : pNum(etcValues[row]));
                } else if (row == '52200') {
                    outsideEquipmentRentals += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '53100') {
                    materials += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '53200') {
                    mats += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '53300') {
                    toolsSupplies += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '53400') {
                    skids += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '54100') {
                    internalSubs += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '54200') {
                    externalSubs += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '54300') {
                    haulingTransportation += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '55100') {
                    personalMeals += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '55200') {
                    teamMealsEntertain += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '55300') {
                    airfare += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '55400') {
                    lodging += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '55900') {
                    otherTravelExpense += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56100') {
                    fieldOfficeRent += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56200') {
                    fieldOfficeUtility += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56300') {
                    fieldOfficeExpense += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56400') {
                    labFeesTesting += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56500') {
                    safetyPPE += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56600') {
                    bondsInsurancePremiums += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56700') {
                    permits += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '56800') {
                    insuranceClaims += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '57100') {
                    taxes += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '57200') {
                    accruedExpenses += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '57300') {
                    warrantyReserve += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '57900') {
                    misc += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '51800') {
                    // Field Bonus ETC no longer counts for versions past the cutoff
                    if (!hideFieldBonus) fieldBonus += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '52300') {
                    fuel += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                } else if (row == '52400') {
                    repairs += (isNaN(etcValues[row]) ? 0 : etcValues[row]);
                }
            });

            const totaloETC = personalMeals + teamMealsEntertain + airfare + lodging + fieldOfficeRent + fieldOfficeUtility + fieldOfficeExpense + labFeesTesting + safetyPPE + bondsInsurancePremiums + permits + insuranceClaims + taxes + accruedExpenses + warrantyReserve + misc + otherTravelExpense;
            const totallETC = regularTime + truckPay + weldingRigPay + fieldBonus;
            const totaleETC = equipment + outsideEquipmentRentals + fuel + repairs;
            const totalmETC = materials + mats + toolsSupplies + skids;
            const totalsETC = internalSubs + externalSubs + haulingTransportation;

            let laborCommitted = 0, equipmentCommitted = 0, rentalsCommitted = 0, materialsCommitted = 0;
            let subcontractorsCommitted = 0, externalsSubcontractorsCommitted = 0, otherExpensesCommitted = 0, ohCommitted = 0;
            let commited = {};

            jobOpenCommitments?.forEach((row) => {
                if (row.Cost_Type_Code >= '51000' && row.Cost_Type_Code <= '51999') {
                    laborCommitted += row.PO_True_Open_Amount;
                }
                if (row.Cost_Type_Code >= '52000' && row.Cost_Type_Code <= '52999') {
                    equipmentCommitted += row.PO_True_Open_Amount;
                    if (row.Cost_Type_Code == '52200') {
                        rentalsCommitted += row.PO_True_Open_Amount;
                    }
                }
                if (row.Cost_Type_Code >= '53000' && row.Cost_Type_Code <= '53999') {
                    materialsCommitted += row.PO_True_Open_Amount;
                }
                if (row.Cost_Type_Code >= '54000' && row.Cost_Type_Code <= '54999') {
                    subcontractorsCommitted += row.PO_True_Open_Amount;
                }
                if (row.Cost_Type_Code >= '54200' && row.Cost_Type_Code <= '54300') {
                    externalsSubcontractorsCommitted += row.PO_True_Open_Amount;
                }
                if (row.Cost_Type_Code >= '55000' && row.Cost_Type_Code <= '58000') {
                    otherExpensesCommitted += row.PO_True_Open_Amount;
                }
                if (row.Cost_Type_Code >= '60900' && row.Cost_Type_Code <= '61000') {
                    ohCommitted += row.PO_True_Open_Amount;
                }
                if (commited[row.Cost_Type_Code]) {
                    commited[row.Cost_Type_Code].value += row.PO_True_Open_Amount;
                } else {
                    commited[row.Cost_Type_Code] = { name: row.Cost_Type_Code, value: row.PO_True_Open_Amount };
                }
            });

            const etcOHAllocation = ((pNum(contractValuesRevised + changeOrderRevenue) + pNum(revenueJTDPrevPeriod)) * pNum(ohPercent) / 100);

            // Incentive computation
            const eacRevenue = contractValuesRevised + changeOrderRevenue;
            const totalCostEAC = (laborJTD + totallETC + laborCommitted) +
                (equipmentJTD + totaleETC + equipmentCommitted) +
                (materialsJTD + totalmETC + materialsCommitted) +
                (subcontractorsJTD + totalsETC + subcontractorsCommitted) +
                (otherExpensesJTD + totaloETC + otherExpensesCommitted) +
                totalcTotal;
            const totalOH = ohJTD + ohCommitted + etcOHAllocation;
            const eacProfit = eacRevenue - totalCostEAC;
            const gm = eacProfit - totalOH;
            const gmPercent = eacRevenue ? gm / eacRevenue : 0;
            const gmPercentDisplay = (gmPercent * 100).toFixed(2);
            const rateVal = pNum(incentiveRateValue) / 100;
            const isEligible = (gmPercent * 100) > 11;

            setIncentiveEligible(isEligible ? 'Yes' : 'No');
            if (!fieldBonus && isEligible) {
                const eligibleAmount = rateVal * (gmPercent - 0.11) / gmPercent * gm;
                setIncentiveEligibleAmount(fNum(eligibleAmount, 1));
                setIncentiveCalcBreakdown(`${incentiveRateValue}% × (${gmPercentDisplay}% − 11%) / ${gmPercentDisplay}% × ${fNum(gm, 1)} = ${fNum(eligibleAmount, 1)}`);
            } else {
                setIncentiveEligibleAmount('');
                setIncentiveCalcBreakdown('');
            }

            const costPayload = {
                contingency: totalcTotal,
                labor: laborJTD + totallETC + laborCommitted,
                materials: materialsJTD + totalmETC + materialsCommitted,
                rentals: (actualTypes['52200'] ? actualTypes['52200'].value : 0) + rentalsCommitted + outsideEquipmentRentals,
                subs: subcontractorsJTD + totalsETC + subcontractorsCommitted,
                other: otherExpensesJTD + totaloETC + otherExpensesCommitted,
                equipment: equipmentJTD + totaleETC + equipmentCommitted,
                overhead: ohJTD + ohCommitted + etcOHAllocation
            };
            setCosts(costPayload);
            setSideMenuCosts(costPayload);
        }
    };

    const RowButton = ({ active, onClick, leftImage, label, budget, eac, budgetRaw, eacRaw, testId }) => {
        // Render a zero/blank as a muted em-dash; otherwise use the formatted value
        const isZero = v => v == null || v === '' || /^[\s$,()\-]*0+(?:\.0+)?[\s$,()\-]*$/.test(String(v));
        const numCell = (val, color) => {
            const z = isZero(val);
            return (
                // a fixed-width cell that never shrinks (a shrinking cell right-aligns each
                // number to a different edge), longhand padding (the shim drops shorthands)
                <Text style={{ textAlign: 'right', fontSize: 10, width: 62, flexShrink: 0, paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, borderRadius: 3, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', color: z ? '#cbd5e1' : (color || '#0F172A'), marginRight: 0 }}>
                    {z ? '—' : val}
                </Text>
            );
        };
        // Variance coloring: red if EAC over budget by >0.1%, green if under by >0.1%
        let eacColor = '#0F172A';
        if (budgetRaw != null && eacRaw != null && budgetRaw !== 0) {
            if (eacRaw > budgetRaw * 1.001) eacColor = '#dc2626';
            else if (eacRaw < budgetRaw * 0.999) eacColor = '#16a34a';
        }
        return (
            <Pressable
                data-test={testId}
                onPress={onClick}
                title={isCollapsed ? label : undefined}
                style={{
                    borderRadius: 5,
                    backgroundColor: active ? '#EFF6FF' : '#fff',
                    borderWidth: 1,
                    borderColor: 'transparent',
                    // longhands: the compat shim drops paddingHorizontal / marginHorizontal,
                    // which left the icons 10 px from the menu's edge
                    paddingTop: 5,
                    paddingBottom: 5,
                    paddingLeft: isCollapsed ? 5 : 10,
                    paddingRight: 5,
                    marginTop: 5,
                    marginBottom: 5,
                    marginLeft: 10,
                    marginRight: 10,
                    flexDirection: 'row',
                    alignItems: 'center',
                    width: 'calc(100% - 20px)',
                    boxSizing: 'border-box', // the padding and border count in the width, as on the header and total rows
                    cursor: 'pointer',
                    justifyContent: isCollapsed ? 'center' : 'flex-start',
                    position: 'relative'
                }}
            >
                {active && <View style={{ position: 'absolute', left: 0, top: 4, bottom: 4, width: 3, backgroundColor: '#3B82F6', borderRadius: 2 }} />}
                <View style={{ marginRight: isCollapsed ? 0 : 8, width: 16, textAlign: 'center', flexDirection: 'row', justifyContent: 'center' }}>
                    {leftImage && <Image source={leftImage} style={{ width: 16, height: 16, filter: active ? 'brightness(0) saturate(100%) invert(69%) sepia(65%) saturate(1946%) hue-rotate(184deg) brightness(53%) contrast(146%)' : 'none' }} />}
                </View>
                {!isCollapsed && <Text style={{ textAlign: 'left', fontSize: 12, flex: 1, minWidth: 40, color: active ? '#1e40af' : '#0F172A', fontWeight: active ? '600' : '400' }}>{label}</Text>}
                {!isCollapsed && (budget !== undefined || eac !== undefined) && (
                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end', flexShrink: 0 }}>
                        <View style={{ marginRight: 4 }}>{numCell(budget)}</View>
                        {numCell(eac, eacColor)}
                    </View>
                )}
            </Pressable>
        );
    };

    const guard = process.env.NODE_ENV !== 'production' || userId === '154045' || userEmailCheck === 'steve.horman@strikeusa.com' || userEmailCheck === 'steven.horman@strikeusa.com';
    const guard2 = guard || userEmailCheck === 'aeron.griggs@strikeusa.com';
    const guardAdmin = isFusionAdmin(userId, userEmailCheck, userRole);
    const backlogAllowed = ['matt', 'tanner', 'weston', 'wayne', 'sammy', 'steve'];
    const guardBacklog = process.env.NODE_ENV !== 'production' || backlogAllowed.includes((userNameValue || '').split(' ')[0].toLowerCase());
    // 3rd-Party Cost Drivers page is still in progress — limit it to Seth, Steve and Troy for now.
    const canSeeWipTabs = process.env.NODE_ENV !== 'production' || userId === '154045' || userEmailCheck === 'seth.bailey@strikeusa.com' || userEmailCheck === 'steve.horman@strikeusa.com' || userEmailCheck === 'steven.horman@strikeusa.com' || userEmailCheck === 'troy.wyles@strikeusa.com';
    // Revenue Curve is still in progress — limit it to Seth and Steve for now (matches laborDashboard).
    const canSeeRevenueCurve = process.env.NODE_ENV !== 'production' || userId === '154045' || userEmailCheck === 'seth.bailey@strikeusa.com' || userEmailCheck === 'steve.horman@strikeusa.com' || userEmailCheck === 'steven.horman@strikeusa.com';
    // Daily Field Report is still in progress — limit it to Seth and Steve for now.
    const canSeeFieldReport = process.env.NODE_ENV !== 'production' || userId === '154045' || userEmailCheck === 'seth.bailey@strikeusa.com' || userEmailCheck === 'steve.horman@strikeusa.com' || userEmailCheck === 'steven.horman@strikeusa.com';
    // Job Team is still in progress — same Seth/Steve gate as the field report.
    const canSeeJobTeam = canSeeFieldReport;

    const handleMenuClick = (e) => {
        // Only toggle if clicking in whitespace (not on a button/tab)
        if (e.target === e.currentTarget || e.target.closest('[data-menu-whitespace]')) {
            setIsCollapsed(!isCollapsed);
        }
    };

    return (
        <ViewTransition>
            <Pressable
                data-print-hide="true"
                onPress={handleMenuClick}
                style={{ zIndex: 10, width: isCollapsed ? 60 : 250, flexShrink: 0, borderRightWidth: 1, borderRightColor: '#00000005', backgroundColor: '#ffffff', boxShadow: '0px 2px 4px 0px #00000005', transition: 'width 0.3s ease', cursor: 'pointer' }}
            >
                <View data-menu-whitespace style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', borderBottomWidth: 1, borderColor: '#ececec', height: 55, position: 'relative' }}>
                    {!isCollapsed && <Image source={strike} style={{ width: 150 }} />}
                    {isCollapsed && <Image source={favicon} style={{ width: 36, height: 36, resizeMode: 'contain' }} />}
                    {apiUrl.toLowerCase().includes('test') && <View style={{ position: 'absolute', bottom: 2, right: isCollapsed ? 2 : 8, backgroundColor: '#f59e0b', borderRadius: 4, padding: '1px 6px' }}>
                        <Text style={{ color: '#fff', fontSize: 9, fontWeight: '700', letterSpacing: 1 }}>TEST</Text>
                    </View>}
                </View>

                {tab ? <View className="side-menu-scroll" style={{ alignItems: isCollapsed ? 'center' : 'unset', position: 'sticky', top: 0, width: isCollapsed ? 60 : null, height: 'calc(100vh - 55px)', overflowY: 'auto', overflowX: 'hidden', paddingBottom: 20 }}>
                    {/* Buyers share the Assistant PM (PC) menu — see buyerAllowedTabs. */}
                    {isPC || isBuyerUser ? (
                    <>
                    <RowButton
                        active={tab === 'Materials'}
                        onClick={() => {
                            setTab('Materials');
                            setSelectedTabStrorage('Materials');
                        }}
                        leftImage={mIcon}
                        label="Materials"
                        budget={fNum(budgets.materialsDayZeroBudget, 1)}
                        eac={fNum(costs.materials, 1)}
                        budgetRaw={budgets.materialsDayZeroBudget}
                        eacRaw={costs.materials}
                    />
                    <RowButton
                        active={tab === 'Rentals'}
                        onClick={() => {
                            setTab('Rentals');
                            setSelectedTabStrorage('Rentals');
                        }}
                        leftImage={backhoeIcon}
                        label="Rentals"
                        budget={fNum(budgets.typeBudgets[52200], 1)}
                        eac={fNum(costs.rentals, 1)}
                        budgetRaw={budgets.typeBudgets[52200]}
                        eacRaw={costs.rentals}
                    />
                    <RowButton
                        active={tab === 'Subcontractors'}
                        onClick={() => {
                            setTab('Subcontractors');
                            setSelectedTabStrorage('Subcontractors');
                        }}
                        leftImage={sIcon}
                        label="Subs"
                        budget={fNum(budgets.subcontractorsDayZeroBudget, 1)}
                        eac={fNum(costs.subs, 1)}
                        budgetRaw={budgets.subcontractorsDayZeroBudget}
                        eacRaw={costs.subs}
                    />
                    <RowButton
                        active={tab === 'Other'}
                        onClick={() => {
                            setTab('Other');
                            setSelectedTabStrorage('Other');
                        }}
                        leftImage={oIcon}
                        label="Other"
                        budget={fNum(budgets.otherExpensesDayZeroBudget, 1)}
                        eac={fNum(costs.other, 1)}
                        budgetRaw={budgets.otherExpensesDayZeroBudget}
                        eacRaw={costs.other}
                    />
                    <RowButton
                        active={tab === 'Purchase Orders'}
                        onClick={() => {
                            setTab('Purchase Orders');
                            setSelectedTabStrorage('Purchase Orders');
                        }}
                        leftImage={oIcon}
                        label="Purchase Orders"
                    />
                    {/* Portfolio procurement views — these span jobs rather than
                        sitting on the selected one, so no per-job budget/EAC. */}
                    <RowButton
                        active={tab === 'Uncommitted'}
                        onClick={() => {
                            setTab('Uncommitted');
                            setSelectedTabStrorage('Uncommitted');
                        }}
                        leftImage={oIcon}
                        label="Uncommitted"
                    />
                    {/* Dates view hidden for now
                    <RowButton
                        active={tab === 'Dates'}
                        onClick={() => {
                            setTab('Dates');
                            setSelectedTabStrorage('Dates');
                        }}
                        leftImage={contingencyIcon}
                        label="Dates"
                    />
                    */}
                    {/* Planning-only views — inside these tabs a PC is locked to the
                        Manpower Planning / Equipment Dashboard subtab (no cost grids),
                        so no budget/EAC figures on the rows either. */}
                    <RowButton
                        active={tab === 'Burn Tool Labor'}
                        onClick={() => {
                            setTab('Burn Tool Labor');
                            setSelectedTabStrorage('Burn Tool Labor');
                        }}
                        leftImage={lIcon}
                        label="Manpower"
                    />
                    <RowButton
                        active={tab === 'Burn Tool Equipment'}
                        onClick={() => {
                            setTab('Burn Tool Equipment');
                            setSelectedTabStrorage('Burn Tool Equipment');
                        }}
                        leftImage={eIcon}
                        label="Equipment"
                    />
                    </>
                    ) : (
                    <>
                    <RowButton
                        active={tab === 'Dashboard'}
                        onClick={() => {
                            setTab('Dashboard');
                            setSelectedTabStrorage('Dashboard');
                        }}
                        leftImage={dashboard}
                        label="Dashboard"
                        testId="dashboard"
                    />

                    <RowButton
                        active={tab === 'Forecast'}
                        onClick={() => {
                            setTab('Forecast');
                            setSelectedTabStrorage('Forecast');
                        }}
                        leftImage={forecast}
                        label="Forecast"
                        testId="forecast"
                    />

                    {/* VoltaGrid jobs: the cost-plus forecast — cost and billable
                        side by side — next to the standard one. */}
                    {isVoltaGridJob(selectedJob) ? (
                        <RowButton
                            active={tab === 'Cost Plus Forecast'}
                            onClick={() => {
                                setTab('Cost Plus Forecast');
                                setSelectedTabStrorage('Cost Plus Forecast');
                            }}
                            leftImage={forecast}
                            label="Cost Plus Forecast"
                            testId="cost-plus-forecast"
                        />
                    ) : null}

                    <RowButton
                        active={tab === 'Overview' || (tab === 'Overview VoltaGrid' && !isVoltaGridJob(selectedJob))}
                        onClick={() => {
                            setTab('Overview');
                            setSelectedTabStrorage('Overview');
                        }}
                        leftImage={overview}
                        label="Overview"
                        testId="overview"
                    />

                    {/* VoltaGrid jobs (helpers/voltaGrid) get their own overview
                        alongside the standard one. */}
                    {isVoltaGridJob(selectedJob) ? (
                        <RowButton
                            active={tab === 'Overview VoltaGrid'}
                            onClick={() => {
                                setTab('Overview VoltaGrid');
                                setSelectedTabStrorage('Overview VoltaGrid');
                            }}
                            leftImage={overview}
                            label="Overview VoltaGrid"
                            testId="overview-voltagrid"
                        />
                    ) : null}

                    {/* Overview - Old (the old Weekly Report layout, overviewOld.js) hidden for now
                    {canSeeWipTabs ? (
                        <RowButton
                            active={tab === 'Overview - Old'}
                            onClick={() => {
                                setTab('Overview - Old');
                                setSelectedTabStrorage('Overview - Old');
                            }}
                            leftImage={overview}
                            label="Overview - Old"
                            testId="overview-old"
                        />
                    ) : null}
                    */}

                    <RowButton
                        active={tab === 'Summary'}
                        onClick={() => {
                            setTab('Summary');
                            setSelectedTabStrorage('Summary');
                        }}
                        leftImage={summary}
                        label="Summary"
                        testId="summary"
                    />

                    <RowButton
                        active={tab === 'Productivity Tracker'}
                        onClick={() => {
                            setTab('Productivity Tracker');
                            setSelectedTabStrorage('Productivity Tracker');
                        }}
                        leftImage={productivity}
                        label="Productivity"
                    />

                    <RowButton
                        active={tab === 'Labor Dashboard'}
                        onClick={() => {
                            setTab('Labor Dashboard');
                            setSelectedTabStrorage('Labor Dashboard');
                        }}
                        leftImage={reports}
                        label="Reports"
                    />

                    {/* Pay Rates (payRateMargin.js) is still in progress — the WIP group only. */}
                    {canSeeWipTabs ? (
                        <RowButton
                            active={tab === 'Pay Rates'}
                            onClick={() => {
                                setTab('Pay Rates');
                                setSelectedTabStrorage('Pay Rates');
                            }}
                            leftImage={reports}
                            label="Pay Rates"
                        />
                    ) : null}

                    {/* Header row for Budget / EAC */}
                    {!isCollapsed && <View style={{ paddingTop: 5, paddingBottom: 5, paddingLeft: 5, paddingRight: 5, marginTop: 12, marginBottom: 0, marginLeft: 10, marginRight: 10, width: 'calc(100% - 20px)', boxSizing: 'border-box', borderWidth: 1, borderColor: 'transparent', flexDirection: 'row', alignItems: 'center' }}>
                        <View style={{ marginRight: 8, width: 16, textAlign: 'center' }} />
                        <Text style={{ textAlign: 'left', fontSize: 12, flex: 1, minWidth: 40 }} />
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', flexShrink: 0 }}>
                            <View style={{ marginRight: 4 }}>
                                <Text style={{ textAlign: 'right', fontSize: 9, width: 62, flexShrink: 0, paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, whiteSpace: 'nowrap', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.6 }}>Budget</Text>
                            </View>
                            <Text style={{ textAlign: 'right', fontSize: 9, width: 62, flexShrink: 0, paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, whiteSpace: 'nowrap', fontWeight: '600', color: '#64748B', textTransform: 'uppercase', letterSpacing: 0.6 }}>EAC</Text>
                        </View>
                    </View>}

                    {/* Budget rows */}
                    <RowButton
                        active={tab === 'Day Zero Budget'}
                        onClick={() => {
                            setTab('Day Zero Budget');
                            setSelectedTabStrorage('Day Zero Budget');
                        }}
                        leftImage={budget}
                        label="Budget"
                        budget={fNum(budgets.contractValuesRevised, 1)}
                    />

                    <RowButton
                        active={tab === 'CO Log'}
                        onClick={() => {
                            setTab('CO Log');
                            setSelectedTabStrorage('CO Log');
                        }}
                        leftImage={colog}
                        label="CO Log"
                        budget={fNum(budgets.changeOrderRevenue, 1)}
                    />

                    {/* Combined total row */}
                    {!isCollapsed && <View style={{ borderRadius: 5, paddingTop: 6, paddingBottom: 6, paddingLeft: 5, paddingRight: 5, marginTop: 4, marginBottom: 0, marginLeft: 10, marginRight: 10, width: 'calc(100% - 20px)', boxSizing: 'border-box', borderWidth: 1, borderColor: 'transparent', borderTopWidth: 1, borderTopColor: '#E5E7EB', backgroundColor: '#F8FAFC', flexDirection: 'row', alignItems: 'center' }}>
                        <View style={{ marginRight: 8, width: 16, textAlign: 'center' }} />
                        <Text style={{ textAlign: 'left', fontSize: 12, flex: 1, minWidth: 40, fontWeight: '600', color: '#0F172A' }} />
                        <View style={{ flexDirection: 'row', justifyContent: 'flex-end', flexShrink: 0 }}>
                            <View style={{ marginRight: 4 }}>
                                <Text style={{ textAlign: 'right', fontSize: 10, width: 62, flexShrink: 0, paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, whiteSpace: 'nowrap', borderRadius: 3, fontWeight: '700', color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>
                                    {fNum(budgets.changeOrderRevenue + budgets.contractValuesRevised, 1)}
                                </Text>
                            </View>
                            <Text style={{ textAlign: 'right', fontSize: 10, width: 62, flexShrink: 0, paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, whiteSpace: 'nowrap', borderRadius: 3 }} />
                        </View>
                    </View>}
                    {!isCollapsed && <View style={{ height: 12 }} />}

                    {/* Cost groups */}
                    <RowButton
                        active={tab === 'Burn Tool Labor'}
                        onClick={() => {
                            setTab('Burn Tool Labor');
                            setSelectedTabStrorage('Burn Tool Labor');
                        }}
                        leftImage={lIcon}
                        label="Labor"
                        budget={fNum(budgets.laborDayZeroBudget, 1)}
                        eac={fNum(costs.labor, 1)}
                        budgetRaw={budgets.laborDayZeroBudget}
                        eacRaw={costs.labor}
                    />
                    <RowButton
                        active={tab === 'Burn Tool Equipment'}
                        onClick={() => {
                            setTab('Burn Tool Equipment');
                            setSelectedTabStrorage('Burn Tool Equipment');
                        }}
                        leftImage={eIcon}
                        label="Equip."
                        budget={fNum(budgets.equipmentDayZeroBudget, 1)}
                        eac={fNum(costs.equipment, 1)}
                        budgetRaw={budgets.equipmentDayZeroBudget}
                        eacRaw={costs.equipment}
                    />
                    <RowButton
                        active={tab === 'Materials'}
                        onClick={() => {
                            setTab('Materials');
                            setSelectedTabStrorage('Materials');
                        }}
                        leftImage={mIcon}
                        label="Materials"
                        budget={fNum(budgets.materialsDayZeroBudget, 1)}
                        eac={fNum(costs.materials, 1)}
                        budgetRaw={budgets.materialsDayZeroBudget}
                        eacRaw={costs.materials}
                    />
                    <RowButton
                        active={tab === 'Rentals'}
                        onClick={() => {
                            setTab('Rentals');
                            setSelectedTabStrorage('Rentals');
                        }}
                        leftImage={backhoeIcon}
                        label="Rentals"
                        budget={fNum(budgets.typeBudgets[52200], 1)}
                        eac={fNum(costs.rentals, 1)}
                        budgetRaw={budgets.typeBudgets[52200]}
                        eacRaw={costs.rentals}
                    />
                    <RowButton
                        active={tab === 'Subcontractors'}
                        onClick={() => {
                            setTab('Subcontractors');
                            setSelectedTabStrorage('Subcontractors');
                        }}
                        leftImage={sIcon}
                        label="Subs"
                        budget={fNum(budgets.subcontractorsDayZeroBudget, 1)}
                        eac={fNum(costs.subs, 1)}
                        budgetRaw={budgets.subcontractorsDayZeroBudget}
                        eacRaw={costs.subs}
                    />
                    <RowButton
                        active={tab === 'Other'}
                        onClick={() => {
                            setTab('Other');
                            setSelectedTabStrorage('Other');
                        }}
                        leftImage={oIcon}
                        label="Other"
                        budget={fNum(budgets.otherExpensesDayZeroBudget, 1)}
                        eac={fNum(costs.other, 1)}
                        budgetRaw={budgets.otherExpensesDayZeroBudget}
                        eacRaw={costs.other}
                    />
                    <RowButton
                        active={tab === 'All Third Party'}
                        onClick={() => {
                            setTab('All Third Party');
                            setSelectedTabStrorage('All Third Party');
                        }}
                        leftImage={oIcon}
                        label="All 3rd Party"
                    />
                    <RowButton
                        active={tab === 'Purchase Orders'}
                        onClick={() => {
                            setTab('Purchase Orders');
                            setSelectedTabStrorage('Purchase Orders');
                        }}
                        leftImage={oIcon}
                        label="Purchase Orders"
                    />
                    <RowButton
                        active={tab === '3rd Party Cost Drivers'}
                        onClick={() => {
                            setTab('3rd Party Cost Drivers');
                            setSelectedTabStrorage('3rd Party Cost Drivers');
                        }}
                        leftImage={oIcon}
                        label="Third Party Spend"
                    />
                    <RowButton
                        active={tab === 'Revenue Curve Forecast'}
                        onClick={() => {
                            setTab('Revenue Curve Forecast');
                            setSelectedTabStrorage('Revenue Curve Forecast');
                        }}
                        leftImage={oIcon}
                        label="Revenue Curve Forecast"
                    />
                    <RowButton
                        active={tab === 'Uncommitted'}
                        onClick={() => {
                            setTab('Uncommitted');
                            setSelectedTabStrorage('Uncommitted');
                        }}
                        leftImage={oIcon}
                        label="Uncommitted"
                    />
                    {/* Dates view hidden for now
                    <RowButton
                        active={tab === 'Dates'}
                        onClick={() => {
                            setTab('Dates');
                            setSelectedTabStrorage('Dates');
                        }}
                        leftImage={contingencyIcon}
                        label="Dates"
                    />
                    */}
                    <RowButton
                        active={tab === 'Contingency'}
                        onClick={() => {
                            setTab('Contingency');
                            setSelectedTabStrorage('Contingency');
                        }}
                        leftImage={contingencyIcon}
                        label="Conting."
                        budget={fNum(budgets.contingencyDayZeroBudget, 1)}
                        eac={fNum(costs.contingency, 1)}
                        budgetRaw={budgets.contingencyDayZeroBudget}
                        eacRaw={costs.contingency}
                    />

                    {/* Totals & KPIs */}
                    {!isCollapsed && (() => {
                        const totalBudget = (budgets.laborDayZeroBudget || 0) + (budgets.equipmentDayZeroBudget || 0) + (budgets.materialsDayZeroBudget || 0) + (budgets.subcontractorsDayZeroBudget || 0) + (budgets.otherExpensesDayZeroBudget || 0) + (budgets.contingencyDayZeroBudget || 0);
                        const totalEac = (costs.labor || 0) + (costs.equipment || 0) + (costs.materials || 0) + (costs.subs || 0) + (costs.other || 0) + (costs.contingency || 0);
                        let totalEacColor = '#0F172A';
                        if (totalBudget !== 0) {
                            if (totalEac > totalBudget * 1.001) totalEacColor = '#dc2626';
                            else if (totalEac < totalBudget * 0.999) totalEacColor = '#16a34a';
                        }
                        return (
                            <View style={{ borderRadius: 5, paddingTop: 6, paddingBottom: 6, paddingLeft: 5, paddingRight: 5, marginTop: 12, marginBottom: 0, marginLeft: 10, marginRight: 10, width: 'calc(100% - 20px)', boxSizing: 'border-box', borderWidth: 1, borderColor: 'transparent', borderTopWidth: 1, borderTopColor: '#E5E7EB', backgroundColor: '#F8FAFC', flexDirection: 'row', alignItems: 'center' }}>
                                <View style={{ marginRight: 8, width: 16, textAlign: 'center' }} />
                                <Text style={{ textAlign: 'left', fontSize: 12, flex: 1, minWidth: 40, fontWeight: '700', color: '#0F172A' }}>Total</Text>
                                <View style={{ flexDirection: 'row', justifyContent: 'flex-end', flexShrink: 0 }}>
                                    <View style={{ marginRight: 4 }}>
                                        <Text style={{ textAlign: 'right', fontSize: 10, width: 62, flexShrink: 0, paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, whiteSpace: 'nowrap', borderRadius: 3, fontWeight: '700', color: '#0F172A', fontVariantNumeric: 'tabular-nums' }}>
                                            {fNum(totalBudget, 1)}
                                        </Text>
                                    </View>
                                    <Text style={{ textAlign: 'right', fontSize: 10, width: 62, flexShrink: 0, paddingTop: 4, paddingBottom: 4, paddingLeft: 4, paddingRight: 4, whiteSpace: 'nowrap', borderRadius: 3, fontWeight: '700', color: totalEacColor, fontVariantNumeric: 'tabular-nums' }}>
                                        {fNum(totalEac, 1)}
                                    </Text>
                                </View>
                            </View>
                        );
                    })()}

                    {!isCollapsed && (() => {
                        const revenue = (budgets.contractValuesRevised || 0) + (budgets.changeOrderRevenue || 0);
                        const totalCost = (costs.labor || 0) + (costs.equipment || 0) + (costs.materials || 0) + (costs.subs || 0) + (costs.other || 0) + (costs.contingency || 0);
                        const jp = revenue - totalCost;
                        const gm = jp - (costs.overhead || 0);
                        const jpPct = revenue ? (jp * 100 / revenue) : null;
                        const gmPct = revenue ? (gm * 100 / revenue) : null;
                        const cellLabel = { fontSize: 9, color: '#64748B', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6 };
                        const cellValue = (color) => ({ fontSize: 12, color: color || '#0F172A', fontWeight: '700', fontVariantNumeric: 'tabular-nums', textAlign: 'right' });
                        const Cell = ({ label, value, color }) => (
                            <View style={{ flex: 1, padding: '6px 8px' }}>
                                <Text style={cellLabel}>{label}</Text>
                                <Text style={cellValue(color)}>{value}</Text>
                            </View>
                        );
                        const pctColor = v => v == null ? '#cbd5e1' : (v >= 11 ? '#16a34a' : v >= 0 ? '#0F172A' : '#dc2626');
                        return (
                            <View style={{ backgroundColor: '#F8FAFC', borderRadius: 6, margin: '8px 10px', borderWidth: 1, borderColor: '#E5E7EB' }}>
                                <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#E5E7EB' }}>
                                    <Cell label="JP" value={fNum(jp, 1)} color={jp >= 0 ? '#0F172A' : '#dc2626'} />
                                    <View style={{ width: 1, backgroundColor: '#E5E7EB' }} />
                                    <Cell label="JP %" value={jpPct == null ? '—' : jpPct.toFixed(1) + '%'} color={pctColor(jpPct)} />
                                </View>
                                <View style={{ flexDirection: 'row' }}>
                                    <Cell label="GM" value={fNum(gm, 1)} color={gm >= 0 ? '#0F172A' : '#dc2626'} />
                                    <View style={{ width: 1, backgroundColor: '#E5E7EB' }} />
                                    <Cell label="GM %" value={gmPct == null ? '—' : gmPct.toFixed(1) + '%'} color={pctColor(gmPct)} />
                                </View>
                            </View>
                        );
                    })()}

                    {/* Billing sits top-level (not in Admin Tools), open to everyone. */}
                    <RowButton
                        active={tab === 'Billing'}
                        onClick={() => {
                            setTab('Billing');
                            setSelectedTabStrorage('Billing');
                        }}
                        leftImage={budget}
                        label="Billing"
                    />

                    <RowButton
                        active={tab === 'PSR Slides'}
                        onClick={() => {
                            setTab('PSR Slides');
                            setSelectedTabStrorage('PSR Slides');
                        }}
                        leftImage={exportIcon}
                        label="Export Reports"
                    />

                    {guardAdmin ? (
                        <RowButton
                            active={tab === 'Admin'}
                            onClick={() => {
                                setTab('Admin');
                                setSelectedTabStrorage('Admin');
                            }}
                            leftImage={adminIcon}
                            label="Admin"
                        />
                    ) : null}

                    {guardAdmin ? (
                        <RowButton
                            active={tab === 'Comms Tools'}
                            onClick={() => {
                                setTab('Comms Tools');
                                setSelectedTabStrorage('Comms Tools');
                            }}
                            leftImage={adminIcon}
                            label="Comms Tools"
                        />
                    ) : null}

                    {guardAdmin ? (
                        <RowButton
                            active={tab === 'Run Projections'}
                            onClick={() => {
                                setTab('Run Projections');
                                setSelectedTabStrorage('Run Projections');
                            }}
                            leftImage={exportIcon}
                            label="Run Projections"
                        />
                    ) : null}

                    {/* Backlog stays top-level for non-admin backlog users (Matt, Tanner, etc.);
                        admins get it inside Admin Tools below. */}
                    {guardBacklog && !guardAdmin ? (
                        <RowButton
                            active={tab === 'Backlog'}
                            onClick={() => {
                                setTab('Backlog');
                                setSelectedTabStrorage('Backlog');
                            }}
                            leftImage={adminIcon}
                            label="Backlog"
                        />
                    ) : null}

                    {/* Admin Tools submenu — hidden entirely from non-admins */}
                    {guardAdmin ? (
                        <Pressable
                            onPress={() => setAdminMenuOpen(!adminMenuOpen)}
                            title={isCollapsed ? 'Admin Tools' : undefined}
                            style={{
                                borderRadius: 5,
                                backgroundColor: '#fff',
                                borderWidth: 1,
                                borderColor: 'transparent',
                                padding: 5,
                                paddingLeft: 10,
                                paddingRight: 10,
                                margin: 5,
                                marginLeft: 10,
                                marginRight: 10,
                                flexDirection: 'row',
                                alignItems: 'center',
                                width: 'calc(100% - 20px)',
                                cursor: 'pointer',
                                justifyContent: isCollapsed ? 'center' : 'flex-start'
                            }}
                        >
                            <View style={{ marginRight: isCollapsed ? 0 : 8, width: 16, flexDirection: 'row', justifyContent: 'center' }}>
                                <Image source={adminIcon} style={{ width: 16, height: 16 }} />
                            </View>
                            {!isCollapsed && <Text style={{ textAlign: 'left', fontSize: 12, color: '#0F172A', fontWeight: '600', flexGrow: 1 }}>Admin Tools</Text>}
                            {!isCollapsed && <Text style={{ fontSize: 10, color: '#64748B' }}>{adminMenuOpen ? '▾' : '▸'}</Text>}
                        </Pressable>
                    ) : null}

                    {guardAdmin && adminMenuOpen ? (
                        <View style={{ paddingLeft: isCollapsed ? 0 : 12, alignItems: isCollapsed ? 'center' : 'unset' }}>
                            {canSeeFieldReport ? (
                                <RowButton
                                    active={tab === 'Daily Field Report'}
                                    onClick={() => {
                                        setTab('Daily Field Report');
                                        setSelectedTabStrorage('Daily Field Report');
                                    }}
                                    leftImage={reports}
                                    label="Field Report"
                                />
                            ) : null}
                            {canSeeJobTeam ? (
                                <RowButton
                                    active={tab === 'Job Team'}
                                    onClick={() => {
                                        setTab('Job Team');
                                        setSelectedTabStrorage('Job Team');
                                    }}
                                    leftImage={sIcon}
                                    label="Job Team"
                                />
                            ) : null}
                            {canSeeWipTabs ? (
                                <RowButton
                                    active={tab === 'Baseline & Weekly'}
                                    onClick={() => {
                                        setTab('Baseline & Weekly');
                                        setSelectedTabStrorage('Baseline & Weekly');
                                    }}
                                    leftImage={reports}
                                    label="Schedule"
                                />
                            ) : null}
                            {canSeeWipTabs ? (
                                <RowButton
                                    active={tab === 'Schedule Status'}
                                    onClick={() => {
                                        setTab('Schedule Status');
                                        setSelectedTabStrorage('Schedule Status');
                                    }}
                                    leftImage={reports}
                                    label="Sched. Status"
                                />
                            ) : null}
                            <RowButton
                                active={tab === 'Projection Sync'}
                                onClick={() => {
                                    setTab('Projection Sync');
                                    setSelectedTabStrorage('Projection Sync');
                                }}
                                leftImage={exportIcon}
                                label="Projection Sync"
                            />
                            <RowButton
                                active={tab === 'Data Diagnostics'}
                                onClick={() => {
                                    setTab('Data Diagnostics');
                                    setSelectedTabStrorage('Data Diagnostics');
                                }}
                                leftImage={adminIcon}
                                label="Data Diagnostics"
                            />
                            <RowButton
                                active={tab === 'Backlog'}
                                onClick={() => {
                                    setTab('Backlog');
                                    setSelectedTabStrorage('Backlog');
                                }}
                                leftImage={adminIcon}
                                label="Backlog"
                            />
                            <RowButton
                                active={tab === 'Change History'}
                                onClick={() => {
                                    setTab('Change History');
                                    setSelectedTabStrorage('Change History');
                                }}
                                leftImage={adminIcon}
                                label="Change History"
                            />
                            <RowButton
                                active={tab === 'Data Cleanup'}
                                onClick={() => {
                                    setTab('Data Cleanup');
                                    setSelectedTabStrorage('Data Cleanup');
                                }}
                                leftImage={adminIcon}
                                label="Data Cleanup"
                            />
                        </View>
                    ) : null}
                    </>
                    )}
                </View> : null}
            </Pressable>
        </ViewTransition>
    );
};

export default SideMenu;


